/**
 * DuoLyst Pricing Page - Truth-Aligned Premium Copy
 * Mobile-first, webview-optimized
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Check, X, Crown, Heart, Globe, Sparkles, ChevronDown, ChevronUp,
  Map, TrendingUp, Camera, Calendar, Shield, Users, Zap, Star, ArrowLeft
} from "lucide-react";
import { useAuth } from "@/App";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

// ==================== PLAN CONFIGURATION ====================

const PLANS = {
  free: {
    id: "free",
    name: "Free",
    tagline: "Track where you've been",
    icon: Globe,
    price: { monthly: 0, yearly: 0 },
    color: "from-slate-500/20 to-slate-600/20",
    borderColor: "border-slate-500/30",
    ctaText: "Current Plan",
    ctaVariant: "outline",
    // Top features (always visible)
    topFeatures: [
      { icon: Map, text: "World Map visualization" },
      { icon: Globe, text: "Country & continent tracking" },
      { icon: TrendingUp, text: "Travel rank progression" },
      { icon: Star, text: "Milestone celebrations" },
      { icon: Sparkles, text: "AI suggestions preview" },
      { icon: Calendar, text: "Route replay teaser" },
    ],
    // Expandable features
    moreFeatures: [
      "Basic trip planner",
      "Itinerary preview",
      "Packing list preview",
      "Budget preview",
      "1 photo per item",
      "1 streak save/month",
      "Basic achievements",
      "Travel search preview",
      "Memories preview",
      "Share cards",
    ],
    // What's NOT included (show as locked)
    lockedFeatures: [
      "Full route replay",
      "Unlimited AI suggestions",
      "City heatmaps",
      "PDF exports",
      "Unlimited photos",
    ],
    limits: {
      items: 10,
      photos: 1,
      streakSaves: 1,
    }
  },
  
  explorer_pro: {
    id: "explorer_pro",
    name: "Explorer Pro",
    tagline: "Understand how you travel",
    icon: Sparkles,
    price: { monthly: 3.99, yearly: 29.99 },
    color: "from-coral/20 to-coral/30",
    borderColor: "border-coral/50",
    ctaText: "Upgrade on duolyst.co.uk",
    ctaVariant: "default",
    popular: true,
    // Top features (always visible)
    topFeatures: [
      { icon: Sparkles, text: "Unlimited AI trip suggestions" },
      { icon: Map, text: "Full route replay history" },
      { icon: TrendingUp, text: "Yearly growth analytics" },
      { icon: Star, text: "Milestone predictions" },
      { icon: Zap, text: "City heatmaps" },
      { icon: Camera, text: "Unlimited photo uploads" },
    ],
    // Expandable features  
    moreFeatures: [
      "Premium insight cards",
      "Destination intelligence",
      "Advanced budgeting & exports",
      "PDF trip exports",
      "Full itinerary builder",
      "Complete packing tools",
      "Unlimited memories",
      "Full scrapbook access",
      "Travel flight discovery",
      "Advanced filters",
      "3 streak saves/month",
      "Comeback missions",
      "Premium share cards",
      "Priority support",
    ],
    inheritedFrom: "Free",
    limits: {
      items: -1,
      photos: -1,
      streakSaves: 3,
    }
  },
  
  duo: {
    id: "duo",
    name: "Duo",
    tagline: "Build your travel story together",
    icon: Heart,
    price: { monthly: 5.99, yearly: 47.99 },
    color: "from-pink-500/20 to-rose-500/20",
    borderColor: "border-pink-500/50",
    ctaText: "Upgrade on duolyst.co.uk",
    ctaVariant: "default",
    // Top features (always visible)
    topFeatures: [
      { icon: Heart, text: "Everything in Explorer Pro" },
      { icon: Users, text: "Partner linking & groups" },
      { icon: Zap, text: "Real-time collaboration" },
      { icon: Map, text: "Shared route replay" },
      { icon: Camera, text: "Collaborative scrapbook" },
      { icon: Shield, text: "2 streak saves each/month" },
    ],
    // Expandable features
    moreFeatures: [
      "Shared trip planning",
      "Shared milestone celebrations",
      "Shared memory timelines",
      "Collaborative budgeting",
      "Shared PDF exports",
      "Group trip features",
      "Anniversary reminders",
      "Joint achievements",
    ],
    inheritedFrom: "Explorer Pro",
    limits: {
      items: -1,
      photos: -1,
      streakSaves: 2,
      partners: 1,
      groups: 5,
    }
  }
};

// ==================== PRICING CARD COMPONENT ====================

const PricingCard = ({ plan, isYearly, onSelect, isCurrentPlan, currency, isNativeApp }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = plan.icon;
  
  const price = isYearly ? plan.price.yearly : plan.price.monthly;
  const period = isYearly ? "/year" : "/month";
  const savings = isYearly && plan.price.monthly > 0 
    ? Math.round((1 - plan.price.yearly / (plan.price.monthly * 12)) * 100)
    : 0;
  
  // Currency conversion (approximate)
  const currencyRates = {
    GBP: { symbol: "£", rate: 1 },
    USD: { symbol: "$", rate: 1.27 },
    EUR: { symbol: "€", rate: 1.17 },
    CAD: { symbol: "C$", rate: 1.72 },
    AUD: { symbol: "A$", rate: 1.95 },
    JMD: { symbol: "J$", rate: 198 },
  };
  
  const curr = currencyRates[currency] || currencyRates.GBP;
  const displayPrice = price === 0 ? "Free" : `${curr.symbol}${(price * curr.rate).toFixed(2)}`;
  
  return (
    <Card data-testid={`pricing-card-${plan.id}`} className={`relative bg-gradient-to-b ${plan.color} ${plan.borderColor} border-2 transition-all duration-300 hover:scale-[1.02] flex flex-col`}>
      {/* Popular badge */}
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-coral text-white px-4 py-1 text-xs font-semibold shadow-lg">
            Most Popular
          </Badge>
        </div>
      )}
      
      <CardHeader className="text-center pb-4 pt-6">
        <div className="flex justify-center mb-3">
          <div className={`p-3 rounded-full ${plan.id === 'duo' ? 'bg-pink-500/20' : plan.id === 'explorer_pro' ? 'bg-coral/20' : 'bg-slate-500/20'}`}>
            <Icon className={`w-6 h-6 ${plan.id === 'duo' ? 'text-pink-400' : plan.id === 'explorer_pro' ? 'text-coral' : 'text-slate-400'}`} />
          </div>
        </div>
        
        <CardTitle className="text-xl font-bold text-foreground">{plan.name}</CardTitle>
        <CardDescription className="text-muted-foreground text-sm mt-1">
          {plan.tagline}
        </CardDescription>
        
        {/* Price */}
        <div className="mt-4">
          <span className="text-3xl font-bold text-foreground">{displayPrice}</span>
          {price > 0 && <span className="text-muted-foreground text-sm">{period}</span>}
          {savings > 0 && (
            <Badge variant="outline" className="ml-2 text-emerald-500 border-emerald-500/50 text-xs">
              Save {savings}%
            </Badge>
          )}
        </div>
        
        {/* Inherited from badge */}
        {plan.inheritedFrom && (
          <p className="text-xs text-muted-foreground mt-2">
            Includes everything in {plan.inheritedFrom}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="flex-1 px-4 pb-2">
        {/* Top Features (Always visible - capped at 6) */}
        <div className="space-y-2.5">
          {plan.topFeatures.slice(0, 6).map((feature, idx) => {
            const FeatureIcon = feature.icon;
            return (
              <div key={idx} className="flex items-center gap-2.5">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-500" />
                </div>
                <span className="text-sm text-foreground">{feature.text}</span>
              </div>
            );
          })}
        </div>
        
        {/* Expandable Features - smooth transition */}
        {plan.moreFeatures.length > 0 && (
          <div className="mt-3">
            <button
              data-testid={`pricing-accordion-${plan.id}`}
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full justify-center py-2"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span>See all {plan.moreFeatures.length} more features</span>
                </>
              )}
            </button>
            
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: expanded ? `${plan.moreFeatures.length * 32 + 16}px` : '0px', opacity: expanded ? 1 : 0 }}
            >
              <div className="space-y-2 pt-2 border-t border-border/50">
                {plan.moreFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-500" />
                    </div>
                    <span className="text-sm text-foreground/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Locked features for Free plan */}
        {plan.lockedFeatures && !expanded && (
          <div className="mt-4 pt-3 border-t border-border/30">
            <p className="text-xs text-muted-foreground mb-2">Upgrade to unlock:</p>
            <div className="space-y-1.5">
              {plan.lockedFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2.5 opacity-50">
                  <X className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="px-4 pb-4 pt-2">
        <Button
          data-testid={`pricing-cta-${plan.id}`}
          onClick={() => onSelect(plan.id)}
          variant={isCurrentPlan ? "outline" : plan.ctaVariant}
          className={`w-full ${
            plan.id === 'explorer_pro' && !isCurrentPlan
              ? 'bg-coral hover:bg-coral/90 text-white'
              : plan.id === 'duo' && !isCurrentPlan
              ? 'bg-pink-500 hover:bg-pink-600 text-white'
              : ''
          }`}
          disabled={isCurrentPlan && plan.id === 'free'}
        >
          {isCurrentPlan ? "Current Plan" : plan.ctaText}
        </Button>
        
        {plan.price.monthly > 0 && !isCurrentPlan && isNativeApp === false && (
          <p className="text-xs text-center text-muted-foreground mt-2 w-full" data-testid={`trial-badge-${plan.id}`}>
            Start with a 7-day free trial &middot; Cancel anytime
          </p>
        )}
      </CardFooter>
    </Card>
  );
};

// ==================== COMPARISON TABLE ====================

const ComparisonTable = ({ currency }) => {
  const features = [
    { name: "World Map & Tracking", free: true, pro: true, duo: true },
    { name: "AI Trip Suggestions", free: "Preview", pro: "Unlimited", duo: "Unlimited + Shared" },
    { name: "Route Replay", free: "Teaser", pro: "Full History", duo: "Full + Shared" },
    { name: "Photos per Item", free: "1", pro: "Unlimited", duo: "Unlimited" },
    { name: "Memories & Scrapbook", free: "Preview", pro: "Full Access", duo: "Collaborative" },
    { name: "Budget Tracking", free: "Preview", pro: "Full + Export", duo: "Shared + Export" },
    { name: "PDF Exports", free: false, pro: true, duo: true },
    { name: "City Heatmaps", free: false, pro: true, duo: true },
    { name: "Yearly Growth Analytics", free: false, pro: true, duo: true },
    { name: "Milestone Predictions", free: false, pro: true, duo: true },
    { name: "Streak Saves/Month", free: "1", pro: "3", duo: "2 each" },
    { name: "Partner Linking", free: "Basic", pro: "Basic", duo: "Full + Groups" },
    { name: "Real-time Collaboration", free: false, pro: false, duo: true },
    { name: "Shared Trip Planning", free: false, pro: false, duo: true },
  ];
  
  const renderCell = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-emerald-500 mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-muted-foreground/50 mx-auto" />;
    return <span className="text-sm text-foreground">{value}</span>;
  };
  
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-2 font-medium text-muted-foreground">Feature</th>
            <th className="text-center py-3 px-2 font-medium text-muted-foreground">Free</th>
            <th className="text-center py-3 px-2 font-medium text-coral">Pro</th>
            <th className="text-center py-3 px-2 font-medium text-pink-400">Duo</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature, idx) => (
            <tr key={idx} className="border-b border-border/50">
              <td className="py-3 px-2 text-foreground">{feature.name}</td>
              <td className="py-3 px-2 text-center">{renderCell(feature.free)}</td>
              <td className="py-3 px-2 text-center">{renderCell(feature.pro)}</td>
              <td className="py-3 px-2 text-center">{renderCell(feature.duo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== MAIN PRICING PAGE ====================

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [currency, setCurrency] = useState("GBP");
  
  // State-gated native detection — prevents first-paint race condition.
  // null = unresolved (show nothing), true = native app, false = web browser
  const [isNativeApp, setIsNativeApp] = useState(null);

  useEffect(() => {
    // Resolve after mount so injectedJavaScriptBeforeContentLoaded has run
    setIsNativeApp(!!window.isNativeApp);
  }, []);
  
  const currencies = ["GBP", "USD", "EUR", "CAD", "AUD", "JMD"];
  
  const handleSelectPlan = async (planId) => {
    if (!user) {
      navigate("/login?redirect=/pricing");
      return;
    }
    
    if (planId === "free") {
      toast.info("You're already on the Free plan");
      return;
    }
    
    try {
      setLoading(true);
      
      // Use the Stripe subscription checkout with trial support
      const billingCycle = isYearly ? "yearly" : "monthly";
      
      // Native app: use bridge for SFSafariViewController
      if (window.isNativeApp && (window.openSafariCheckout || window.ReactNativeWebView)) {
        const response = await axios.post(
          `${API}/subscription/checkout/mobile`,
          { plan_id: planId, billing_cycle: billingCycle },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.checkout_url) {
          if (window.openSafariCheckout) {
            window.openSafariCheckout(response.data.checkout_url);
          } else if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: "OPEN_SAFARI_CHECKOUT", url: response.data.checkout_url }));
          }
        }
        return;
      }
      
      // Web: synchronous GET redirect — iOS Safari safe, includes trial
      const authToken = token || axios.defaults.headers.common["Authorization"]?.replace("Bearer ", "");
      window.location.href = `${API}/subscription/checkout/redirect?plan_id=${planId}&billing_cycle=${billingCycle}&token=${authToken}`;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Back navigation */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center">
          <Button variant="ghost" size="sm" onClick={() => user ? navigate("/dashboard") : navigate("/")} data-testid="pricing-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {user ? "Dashboard" : "Home"}
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-background py-10 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="outline" className="mb-4 text-coral border-coral/50">
            <Crown className="w-3 h-3 mr-1" />
            Premium Plans
          </Badge>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Unlock Your Full Travel Story
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            From tracking your adventures to reliving every memory. 
            Choose the plan that matches your journey.
          </p>
          
          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm ${!isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <Switch
              data-testid="pricing-billing-toggle"
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <span className={`text-sm ${isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              Yearly
            </span>
            {isYearly && (
              <Badge className="bg-emerald-500/20 text-emerald-500 text-xs">
                Save up to 37%
              </Badge>
            )}
          </div>
          
          {/* Currency selector */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="text-xs text-muted-foreground">Currency:</span>
            <select
              data-testid="pricing-currency-selector"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="text-xs bg-background border border-border rounded px-2 py-1 text-foreground"
            >
              {currencies.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          <PricingCard
            plan={PLANS.free}
            isYearly={isYearly}
            onSelect={handleSelectPlan}
            isCurrentPlan={!user?.subscription?.plan_id || user.subscription.plan_id === "free"}
            currency={currency}
            isNativeApp={isNativeApp}
          />
          <PricingCard
            plan={PLANS.explorer_pro}
            isYearly={isYearly}
            onSelect={handleSelectPlan}
            isCurrentPlan={user?.subscription?.plan_id === "explorer_pro"}
            currency={currency}
            isNativeApp={isNativeApp}
          />
          <PricingCard
            plan={PLANS.duo}
            isYearly={isYearly}
            onSelect={handleSelectPlan}
            isCurrentPlan={user?.subscription?.plan_id === "duo"}
            currency={currency}
            isNativeApp={isNativeApp}
          />
        </div>
        
        {/* Comparison toggle */}
        <div className="text-center mt-10">
          <button
            data-testid="pricing-comparison-toggle"
            onClick={() => setShowComparison(!showComparison)}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showComparison ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showComparison ? "Hide comparison" : "Compare all features"}
          </button>
        </div>
        
        {/* Comparison Table */}
        {showComparison && (
          <div className="mt-8 bg-card rounded-xl border border-border p-4 animate-in slide-in-from-top-4 duration-300">
            <ComparisonTable currency={currency} />
          </div>
        )}
        
        {/* Trust badges — web only, hidden in native iOS/Android app */}
        {isNativeApp === false && (
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>7-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span>Instant access</span>
            </div>
          </div>
        )}
        
        {/* FAQ or support */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          Questions? <a href="mailto:support@duolyst.co.uk" className="text-coral hover:underline">Contact support</a>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 py-8 px-4 mt-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="DuoLyst" className="h-8 w-auto" />
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <a href="/about" className="hover:text-foreground transition-colors">About</a>
              <a href="/contact" className="hover:text-foreground transition-colors">Contact</a>
              <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-foreground transition-colors">Terms</a>
              <a href="mailto:support@duolyst.co.uk" className="hover:text-foreground transition-colors">Support</a>
            </div>
            <p className="text-xs text-muted-foreground/60">
              DuoLyst Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
